import React, { useCallback } from "react";

import makeStyles from "@mui/styles/makeStyles";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";

import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import { green, red, yellow, grey } from "@mui/material/colors";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";

const useStyles = makeStyles(theme => ({
  itemContent: {
    height: "auto",
    fontSize: "1.25rem",
    borderBottom: "solid",
    borderBottomColor: grey[200],
    backgroundColor: "white",
  },

  listItemText: {
    textTransform: "none",
  },

  contentTitle: {
    fontSize: "2.00rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(4),
  },

  labelAndValue: {
    "& label": {
      fontWeight: "bold",
      paddingRight: theme.spacing(2),
    },
  },

  active: {
    textTransform: "uppercase",
    color: green[500],
  },

  inactive: {
    textTransform: "uppercase",
    color: red[500],
  },

  restricted: {
    textTransform: "uppercase",
    color: yellow[500],
  },

  actionContainer: {
    "& button": {
      margin: "10px 0",
    },
  },
}));

const ApplicationAccountListItem = ({
  account,
  selected,
  onEditClick,
  onDeactivateClick,
  onActivateClick,
  onKeysClick,
  onSelect,
}) => {
  const classes = useStyles();

  const handleActiveClicked = useCallback(() => {
    onSelect(account);
    onActivateClick(account);
  }, [onSelect, onActivateClick, account]);

  const handleDeactivateClicked = useCallback(() => {
    onSelect(account);
    onDeactivateClick(account);
  }, [onSelect, onDeactivateClick, account]);

  const handleEditClicked = useCallback(() => {
    onSelect(account);
    onEditClick(account);
  }, [onSelect, onEditClick, account]);

  const handleShowKeysClicked = useCallback(() => {
    onSelect(account);
    onKeysClick(account);
  }, [onSelect, onKeysClick, account]);

  return (
    <ListItem
      selected={selected}
      classes={{ root: classes.itemContent }}
      key={account.applicationId}
      onClick={() => onSelect(account)}
    >
      <ListItemText
        classes={{ root: classes.listItemText }}
        disableTypography={true}
        primary={
          <React.Fragment>
            <div className={classes.contentTitle}>
              {account.applicationName}
            </div>
          </React.Fragment>
        }
        secondary={
          <Grid container justifyContent="flex-start" spacing={2}>
            <Grid item sm={5}>
              <div className={classes.labelAndValue}>
                <label>Application Account Id:</label>
                {account.applicationId}
              </div>
            </Grid>

            <Grid item sm={3}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  {account.active ? (
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.active }}
                    >
                      Active
                    </Typography>
                  ) : (
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.inactive }}
                    >
                      Inactive
                    </Typography>
                  )}
                </Grid>
                {account.accessRestricted && (
                  <Grid item>
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.restricted }}
                    >
                      Restricted
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item sm={4}>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Created By:</label>
                    {account.createdByUsername}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Date Created:</label>
                    {account.dateCreated}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Updated By:</label>
                    {account.updatedByUsername}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Date Updated:</label>
                    {account.dateUpdated}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
      />
      <ListItemSecondaryAction classes={{ root: classes.actionContainer }}>
        <Tooltip title="Edit Account">
          <IconButton
            autoFocus={selected}
            edge="end"
            aria-label="edit"
            onClick={handleEditClicked}
            size="large"
          >
            <EditIcon fontSize="large" />
            <Typography sx={visuallyHidden}>Edit Account</Typography>
          </IconButton>
        </Tooltip>

        <Tooltip title="Show Keys">
          <IconButton
            edge="end"
            aria-label="show keys"
            onClick={handleShowKeysClicked}
            size="large"
          >
            <VpnKeyIcon fontSize="large" />
            <Typography sx={visuallyHidden}>Show Keys</Typography>
          </IconButton>
        </Tooltip>

        {account.active ? (
          <Tooltip title="Deactivate Account">
            <IconButton
              edge="end"
              aria-label="deactivate"
              onClick={handleDeactivateClicked}
              size="large"
            >
              <RemoveCircleOutlineIcon fontSize="large" />
              <Typography sx={visuallyHidden}>Deactivate Account</Typography>
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Activate Account">
            <IconButton
              edge="end"
              aria-label="activate"
              onClick={handleActiveClicked}
              size="large"
            >
              <CheckCircleOutlineIcon fontSize="large" />
              <Typography sx={visuallyHidden}>Activate Account</Typography>
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default ApplicationAccountListItem;
